import { useEffect, useState } from "react";
import type { CtcaeTerm, Grade } from "../types";

type Props = {
  record: CtcaeTerm;
  automaticGrade?: Grade;
  possibleGrades?: Grade[];
};

export function GradeCards({ record, automaticGrade, possibleGrades }: Props) {
  const [copiedGrade, setCopiedGrade] = useState<Grade>();

  useEffect(() => setCopiedGrade(undefined), [record.id]);

  const copyGrade = async (grade: Grade, criteria: string) => {
    await navigator.clipboard.writeText(`${record.term} — Grade ${grade}: ${criteria} — CTCAE v${record.version}`);
    setCopiedGrade(grade);
    window.setTimeout(() => setCopiedGrade((current) => current === grade ? undefined : current), 1800);
  };

  return (
    <div className="grade-grid" aria-label="Grade criteria">
      {([1, 2, 3, 4, 5] as Grade[]).map((grade) => {
        const selected = automaticGrade === grade;
        const possible = possibleGrades?.includes(grade);
        const text = record.grades[String(grade) as keyof CtcaeTerm["grades"]];
        return (
          <article
            className={`grade-card${selected ? " selected" : ""}${possible ? " possible" : ""}${!text ? " empty" : ""}`}
            key={grade}
          >
            <span className="grade-card-label">
              <strong>{selected ? "✓ " : ""}Grade {grade}</strong>
              <span className="grade-card-actions">
                {selected && <em>Value match</em>}
                {possible && <em>Possible</em>}
                <button
                  className={`grade-copy-button${copiedGrade === grade ? " copied" : ""}`}
                  type="button"
                  disabled={!text}
                  aria-label={!text ? `Grade ${grade} criteria unavailable` : copiedGrade === grade ? `Copied Grade ${grade}` : `Copy Grade ${grade} criteria`}
                  title={!text ? undefined : copiedGrade === grade ? "Copied" : `Copy Grade ${grade}`}
                  onClick={() => text && copyGrade(grade, text)}
                >
                  {copiedGrade === grade
                    ? <span className="copy-check" aria-hidden="true">✓</span>
                    : <span className="copy-glyph" aria-hidden="true" />}
                </button>
              </span>
            </span>
            <span className="grade-card-text">{text ?? <>—<small>Not available for this CTCAE term</small></>}</span>
          </article>
        );
      })}
    </div>
  );
}
